import { Link } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import GlobalNav from '@/components/GlobalNav';
import { ContactButton } from '@/components/ContactForm';

const FAQ_DATA = [
  {
    question: "What is ScoreCite?",
    answer: "ScoreCite is a diagnostic tool that shows how AI assistants and answer engines see your website. It turns technical checks, content structure and trust signals into clear scores and a practical improvement plan."
  },
  {
    question: "How is ScoreCite different from typical SEO tools?",
    answer: "Most tools focus on classic rankings and backlinks. ScoreCite is built for the AI era: it measures how \"citation-ready\" your pages are for systems like ChatGPT, Perplexity and Google AI Overviews, and what you need to change so they can safely quote you as a source."
  },
  {
    question: "Does ScoreCite automatically fix my SEO or rewrite content?",
    answer: "No. ScoreCite does not promise magic automation. It audits your site, explains what is wrong or missing, and gives you a prioritized list of concrete changes. You stay in control of what to edit and when."
  },
  {
    question: "Who is ScoreCite for?",
    answer: "ScoreCite is for developers, solo founders, small businesses and marketing teams who want a realistic view of their website in AI search. If you build or maintain websites and care about how they show up in AI answers, ScoreCite is for you."
  },
  {
    question: "What do I get in the Free plan?",
    answer: "The Free plan gives you a baseline check for lead generation: up to 10 pages, scores and charts, and issue counts without detailed breakdown. It is ideal to quickly see whether a site has fundamental problems before going deeper."
  },
  {
    question: "What does the Core plan include?",
    answer: "Core is the full audit for one domain. You get complete page-level analysis, KPI dashboard, AI-generated recommendations, domain monitoring and PDF export. It is a fully featured plan designed for one main site."
  },
  {
    question: "What does the Pro plan add on top of Core?",
    answer: "Pro includes everything in Core, but for multiple domains. You can monitor up to three sites, compare them, run competitor analysis and get priority support. It is built for agencies, product portfolios and more advanced teams."
  },
  {
    question: "How often can I run audits?",
    answer: "You can re-run audits to track progress over time. The dashboard stores your audit history and KPI changes, so you can see how each batch of edits impacts SEO Health, AI Readiness and Trust scores."
  },
  {
    question: "What exactly does ScoreCite check on my pages?",
    answer: "ScoreCite analyses technical SEO (indexability, meta tags, headings, duplicates), answer-friendly structure (definitions, steps, FAQs, tables, content depth) and trust signals (business data, contact details, policies, case studies, numbers and statistics). All of this is reflected in your scores and recommendations."
  },
  {
    question: "Will ScoreCite improve my Google rankings?",
    answer: "ScoreCite is not a ranking guarantee. By fixing the issues it highlights, you make your site clearer, more structured and more trustworthy, which usually helps both traditional search and AI answer engines – but the implementation is in your hands."
  },
  {
    question: "Does ScoreCite work with any tech stack or CMS?",
    answer: "Yes. ScoreCite looks at your public pages, not at your codebase. It works with sites built on WordPress, Webflow, custom React/Next apps, static sites and almost any other platform."
  },
  {
    question: "Is my data safe?",
    answer: "ScoreCite only analyses the URLs you choose. It does not require admin access to your CMS or analytics. Audit data is stored securely and used only to generate your reports and dashboards."
  },
  {
    question: "Can I use ScoreCite for client projects?",
    answer: "Yes. Many users run audits for client sites, export PDFs and use the action plans as the basis for proposals and implementation sprints. Pro is especially suited to agencies and consultants handling multiple domains."
  }
];

export default function FAQPage() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_DATA.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>ScoreCite FAQ – Answers to common questions about AI site audits</title>
        <meta name="description" content="ScoreCite FAQ: What makes us different from SEO tools, what each plan includes, how audits work, and everything else you need to know about making your site AI citation-ready." />
        <meta name="robots" content="index, follow" />
        <meta name="keywords" content="ScoreCite FAQ, AI search audit, answer engine optimization, site citation readiness" />
      </Helmet>

      {/* JSON-LD Schema */}
      <script 
        type="application/ld+json" 
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} 
      />

      <GlobalNav />

      <div className="min-h-screen bg-[#0A0A0A] py-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">Frequently Asked Questions</h1>
            <p className="text-zinc-400">Everything you need to know about ScoreCite and AI-ready site audits.</p>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {FAQ_DATA.map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg p-6 hover:border-white/20 transition-colors"
              >
                <h2 className="text-white font-semibold text-lg mb-3">{item.question}</h2>
                <p className="text-zinc-400 text-sm leading-relaxed">{item.answer}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center p-8 bg-gradient-to-r from-blue-600/10 to-purple-600/10 border border-white/10 rounded-lg">
            <h3 className="text-white font-semibold text-lg mb-3">Still have questions?</h3>
            <p className="text-zinc-400 text-sm mb-6">We're here to help. Reach out and we'll get back to you.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <ContactButton className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-sm border-0 hover:text-white" />
              <Link to="/pricing">
                <button className="px-6 py-3 border border-white/20 text-white font-semibold rounded-sm hover:bg-white/5 transition-colors">
                  View Pricing
                </button>
              </Link>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <Link to="/" className="text-blue-400 hover:text-blue-300 text-sm">
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
